import React, { useState, useEffect } from 'react';
import Modal from '@mui/material/Modal';
import { Button } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import {
  AUDIT_UNKNOWN_ACTOR_ID,
  IAuditLogActor,
  IAuditLogDatabaseFilters,
} from 'api/bitedb/audit/audit.d';

interface AuditLogFilterModalProps {
  open: boolean;
  onClose: () => void;
  /** Distinct action types to offer -- fetched by the caller with whichever
   * scope applies (database-wide, one entity, etc.), so this modal stays
   * reusable across scopes. */
  availableActions: string[];
  noActionsMessage?: string;
  /** People who appear in the feed. Omit to hide the People column. */
  availablePeople?: IAuditLogActor[];
  noPeopleMessage?: string;
  filters: IAuditLogDatabaseFilters;
  onApply: (filters: IAuditLogDatabaseFilters) => void;
}

// Events recorded before audit writes named their action all share this one.
const GENERIC_AUDIT_ACTION = 'bitedb.audit.recorded';

// "bulk.entity.status.transition" -> "Bulk Entity Status Transition"
export const formatAuditActionLabel = (action: string): string =>
  action === GENERIC_AUDIT_ACTION
    ? 'Other activity'
    : action
        .split(/[._]/)
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

export const auditActorId = (actor: IAuditLogActor): string =>
  actor.user_id ?? AUDIT_UNKNOWN_ACTOR_ID;

export const auditActorLabel = (actor: IAuditLogActor): string =>
  actor.user_id ? actor.user_name || 'Unknown user' : 'System / Unknown';

const AuditLogFilterModal: React.FC<AuditLogFilterModalProps> = ({
  open,
  onClose,
  availableActions,
  noActionsMessage = 'No actions recorded yet.',
  availablePeople,
  noPeopleMessage = 'No one has recorded activity yet.',
  filters,
  onApply,
}) => {
  const [startDate, setStartDate] = useState(filters.startDate || '');
  const [endDate, setEndDate] = useState(filters.endDate || '');
  const [selectedActions, setSelectedActions] = useState<string[]>(
    filters.actions || [],
  );
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>(
    filters.userIds || [],
  );

  useEffect(() => {
    if (open) {
      setStartDate(filters.startDate || '');
      setEndDate(filters.endDate || '');
      setSelectedActions(filters.actions || []);
      setSelectedUserIds(filters.userIds || []);
    }
  }, [open, filters.startDate, filters.endDate, filters.actions, filters.userIds]);

  const toggle = (value: string) => (prev: string[]) =>
    prev.includes(value)
      ? prev.filter((item) => item !== value)
      : [...prev, value];

  const handleApply = () => {
    onApply({
      ...filters,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      actions: selectedActions,
      userIds: availablePeople ? selectedUserIds : filters.userIds,
    });
    onClose();
  };

  const handleClear = () => {
    setStartDate('');
    setEndDate('');
    setSelectedActions([]);
    setSelectedUserIds([]);
  };

  const renderOption = (
    option: { value: string; label: string },
    isSelected: boolean,
    onClick: () => void,
  ) => (
    <div
      key={option.value}
      className='bill-container-fit-screen-content-row-checkbox'
      onClick={onClick}
    >
      <div className='bill-container-fit-screen-content-row-input'>
        <div
          className={`bill-container-fit-screen-content-row-input-icon ${
            isSelected ? 'selected' : ''
          }`}
        >
          <div
            className={`bill-container-fit-screen-content-row-input-icon-rnd ${
              isSelected ? 'selected' : ''
            }`}
          />
        </div>
      </div>
      <div className='bill-container-fit-screen-content-row-checkbox-text'>
        <p>{option.label}</p>
      </div>
    </div>
  );

  return (
    <Modal open={open} onClose={onClose}>
      <div
        className='filter-options-container'
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '90%',
          maxWidth: availablePeople ? '960px' : '760px',
          maxHeight: '90vh',
          overflow: 'hidden',
        }}
      >
        <div className='filter-options-header'>
          <div className='filter-options-header-content'>
            <div className='filter-options-header-icon'>
              <FilterListIcon sx={{ fontSize: 24, color: '#4895EF' }} />
            </div>
            <div className='filter-options-header-text-group'>
              <div className='filter-options-header-text-primary'>
                Audit Log Filters
              </div>
              <div className='filter-options-header-text-secondary'>
                {availablePeople
                  ? 'Narrow the activity feed by date range, action type and person.'
                  : 'Narrow the activity feed by date range and action type.'}
              </div>
            </div>
          </div>
          <div className='filter-options-header-close' onClick={onClose}>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='24'
              height='24'
              viewBox='0 0 24 24'
              fill='none'
            >
              <path
                d='M18 6L6 18M6 6L18 18'
                stroke='var(--card-text)'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </div>
        </div>

        <div className='filter-options-body-container'>
          <div
            className='filter-column-container'
            style={{ flex: 1, minWidth: 0 }}
          >
            <div className='filter-column-header'>
              <div className='filter-column-header-title'>Date Range</div>
            </div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
              }}
            >
              <label
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                  fontSize: '12px',
                  color: 'var(--card-text)',
                }}
              >
                From
                <input
                  type='date'
                  value={startDate}
                  max={endDate || undefined}
                  onChange={(event) => setStartDate(event.target.value)}
                  style={{
                    padding: '8px 10px',
                    borderRadius: '8px',
                    border: '1px solid #2E3036',
                    background: 'var(--Background-200, var(--bg-colors))',
                    color: 'var(--text-primary)',
                  }}
                />
              </label>
              <label
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                  fontSize: '12px',
                  color: 'var(--card-text)',
                }}
              >
                To
                <input
                  type='date'
                  value={endDate}
                  min={startDate || undefined}
                  onChange={(event) => setEndDate(event.target.value)}
                  style={{
                    padding: '8px 10px',
                    borderRadius: '8px',
                    border: '1px solid #2E3036',
                    background: 'var(--Background-200, var(--bg-colors))',
                    color: 'var(--text-primary)',
                  }}
                />
              </label>
            </div>
          </div>

          <div
            className='filter-column-container'
            style={{ flex: 1, minWidth: 0 }}
          >
            <div className='filter-column-header'>
              <div className='filter-column-header-title'>Action Type</div>
            </div>
            <div
              className='filter-column-options-container'
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                maxHeight: '360px',
                overflowY: 'auto',
              }}
            >
              {availableActions.length === 0 && (
                <p style={{ color: 'var(--card-text)', fontSize: '13px' }}>
                  {noActionsMessage}
                </p>
              )}
              {availableActions.map((action) =>
                renderOption(
                  { value: action, label: formatAuditActionLabel(action) },
                  selectedActions.includes(action),
                  () => setSelectedActions(toggle(action)),
                ),
              )}
            </div>
          </div>

          {availablePeople ? (
            <div
              className='filter-column-container'
              style={{ flex: 1, minWidth: 0 }}
            >
              <div className='filter-column-header'>
                <div className='filter-column-header-title'>People</div>
              </div>
              <div
                className='filter-column-options-container'
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                  maxHeight: '360px',
                  overflowY: 'auto',
                }}
              >
                {availablePeople.length === 0 && (
                  <p style={{ color: 'var(--card-text)', fontSize: '13px' }}>
                    {noPeopleMessage}
                  </p>
                )}
                {availablePeople.map((actor) => {
                  const id = auditActorId(actor);
                  return renderOption(
                    { value: id, label: auditActorLabel(actor) },
                    selectedUserIds.includes(id),
                    () => setSelectedUserIds(toggle(id)),
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>

        <div className='filter-options-btns-container'>
          <div className='filter-options-btns-spacer'>
            <Button sx={{ color: 'var(--text-primary)' }} onClick={handleClear}>
              Clear
            </Button>
            <Button
              sx={{ color: 'var(--text-primary)', background: '#4895EF' }}
              onClick={handleApply}
            >
              Apply
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default AuditLogFilterModal;
